import { Building2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { queryClient } from '@/config/query-client'
import { CreateWorkspaceDialog } from '@/features/layout/components/CreateWorkspaceDialog'
import { workspaceLogin as workspaceLoginApi } from '@/services/api/auth.service'
import { ApiError } from '@/services/api/client'
import { getSubscriptionStatus } from '@/services/api/subscription.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { useWorkspaceStore } from '@/stores/workspace.store'




interface Workspace {
  id: string
  name: string
}

export function WorkspaceSelectCard() {
  const navigate = useNavigate()
  const { userId, workspaces, logout } = useAuthStore()
  const { setWorkspace } = useWorkspaceStore()
  const permissionStore = usePermissionStore()
  const subscriptionStore = useSubscriptionStore()
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const handleLogout = () => {
    sessionStorage.removeItem('returnUrl')
    logout()
    queryClient.clear()
    permissionStore.clear()
    subscriptionStore.clear()
    navigate('/login')
  }

  // The login token only lives ~10 minutes. When it (or the access token) is rejected the user
  // has to sign in again; there is nothing to retry.
  const handleSessionExpired = () => {
    toast.error('Your sign-in has expired. Please sign in again.')
    handleLogout()
  }

  const handleWorkspaceSelect = async (workspace: Workspace) => {
    if (!userId) {
      navigate('/login')
      return
    }

    setSelectedWorkspace(workspace)
    setIsLoading(true)

    try {
      const { loginToken } = useAuthStore.getState()
      const response = await workspaceLoginApi({
        userId,
        workspaceId: workspace.id,
        loginToken: loginToken ?? undefined,
      })

      useAuthStore.getState().setAccessToken(response.accessToken)
      useAuthStore.getState().setLoginToken(null)
      setWorkspace(workspace.id, workspace.name)

      // Store superUser / developer flags directly from the login response
      permissionStore.setIsSuperUser(response.superUser)
      permissionStore.setIsDeveloper(response.developerRole)

      // Effective permissions load via usePermission() (GET /permissions/me) for the new workspace.

      try {
        const subscription = await getSubscriptionStatus()
        subscriptionStore.setStatus(subscription.status)
        subscriptionStore.setCancelAtPeriodEnd(subscription.cancelAtPeriodEnd)
      } catch {
        // non-fatal
      }

      const returnUrl = sessionStorage.getItem('returnUrl')
      if (returnUrl) {
        sessionStorage.removeItem('returnUrl')
        navigate(returnUrl, { replace: true })
      } else {
        navigate('/')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        handleSessionExpired()
      } else {
        toast.error('Failed to access workspace. Please try again.')
      }
      setSelectedWorkspace(null)
    } finally {
      setIsLoading(false)
    }
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>No Workspaces</CardTitle>
          <CardDescription>
            You don't have any workspaces yet. Create one to get started.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3">
          <Button className="flex-1" onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create workspace
          </Button>
          <Button variant="outline" onClick={handleLogout}>
            Back to Login
          </Button>
        </CardContent>

        <CreateWorkspaceDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
        />
      </Card>
    )
  }

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-bold">Select a Workspace</h1>
        <p className="mt-2 text-neutral-500">
          Choose which workspace you want to access
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {workspaces.map((workspace) => (
          <Card
            key={workspace.id}
            variant="interactive"
            className={`relative ${selectedWorkspace?.id === workspace.id ? 'ring-2 ring-primary-500' : ''
              }`}
            onClick={() => handleWorkspaceSelect(workspace)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                  <Building2 className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{workspace.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                disabled={isLoading && selectedWorkspace?.id === workspace.id}
              >
                {isLoading && selectedWorkspace?.id === workspace.id
                  ? 'Loading...'
                  : 'Open Workspace'}
              </Button>
            </CardContent>
          </Card>
        ))}

        {/* Create new workspace card */}
        <Card
          variant="interactive"
          className="flex flex-col items-center justify-center border-dashed"
          onClick={() => setShowCreateDialog(true)}
        >
          <CardHeader className="pb-2 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
              <Plus className="h-5 w-5 text-neutral-500" />
            </div>
            <CardTitle className="text-lg text-neutral-600">New Workspace</CardTitle>
            <CardDescription className="text-xs">Create a workspace</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Create
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 text-center">
        <Button variant="ghost" onClick={handleLogout}>
          Log out
        </Button>
      </div>

      {/* Create workspace dialog */}
      <CreateWorkspaceDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  )
}